import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  Wrench, 
  Zap, 
  Droplets, 
  PaintBucket, 
  DoorOpen, 
  Sofa,
  Phone,
  Clock,
  Shield,
  Star,
  CheckCircle,
  MessageCircle,
  ArrowRight,
  Instagram,
  FileText,
  Building2,
  LayoutDashboard,
  CreditCard,
  Receipt,
  BadgeCheck,
  Wrench as WrenchIcon
} from 'lucide-react';

const WHATSAPP_NUMBER = '5531973381052';
const WHATSAPP_MESSAGE = 'Olá! Gostaria de solicitar um orçamento para serviços de assistência residencial.';
const WHATSAPP_URL = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(WHATSAPP_MESSAGE)}`;

// Hero images carousel
const heroImages = [
  {
    url: "https://images.unsplash.com/photo-1621905251918-48416bd8575a?ixlib=rb-4.1.0&auto=format&fit=crop&w=800&q=80",
    alt: "Profissional realizando reparo elétrico",
    service: "Elétrica"
  },
  {
    url: "https://images.pexels.com/photos/7218683/pexels-photo-7218683.jpeg?auto=compress&cs=tinysrgb&w=800",
    alt: "Profissional pintando parede",
    service: "Pintura"
  },
  {
    url: "https://images.pexels.com/photos/35290675/pexels-photo-35290675.jpeg?auto=compress&cs=tinysrgb&w=800",
    alt: "Profissional fazendo reparo hidráulico",
    service: "Hidráulica"
  },
  {
    url: "https://images.unsplash.com/photo-1504148455328-c376907d081c?ixlib=rb-4.1.0&auto=format&fit=crop&w=800&q=80",
    alt: "Profissional montando móveis",
    service: "Desmontagem/Montagem de Móveis"
  }
];

const services = [
  { icon: Zap, title: 'Elétrica', description: 'Instalações, reparos e manutenção elétrica' },
  { icon: Droplets, title: 'Hidráulica', description: 'Consertos de vazamentos, instalação de torneiras e chuveiros' },
  { icon: PaintBucket, title: 'Pintura', description: 'Pintura de ambientes internos e externos' },
  { icon: DoorOpen, title: 'Portas e Fechaduras', description: 'Instalação e reparo de portas, fechaduras e dobradiças' },
  { icon: Sofa, title: 'Desmontagem e Montagem de Móveis', description: 'Montagem e desmontagem de móveis em geral' },
  { icon: Wrench, title: 'Reparos Gerais', description: 'Pequenos reparos e manutenções diversas' },
];

const benefits = [
  { icon: Clock, title: 'Agilidade', description: 'Atendimento rápido e eficiente' },
  { icon: Shield, title: 'Segurança', description: 'Profissionais qualificados e de confiança' },
  { icon: Star, title: 'Qualidade', description: 'Serviços com garantia e excelência' },
];

const LandingPage = () => {
  const [currentImage, setCurrentImage] = useState(0);

  // Auto-rotate images every 4 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImage((prev) => (prev + 1) % heroImages.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-sm z-50 border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <div className="flex items-center">
              <img src="/logo-header.png" alt="Tenance" className="h-20" />
            </div>
            <nav className="hidden md:flex items-center space-x-8">
              <a href="#servicos" className="text-gray-600 hover:text-[#2B7A3D] transition-colors font-medium">Serviços</a>
              <a href="#sobre" className="text-gray-600 hover:text-[#2B7A3D] transition-colors font-medium">Sobre</a>
              <a href="#contato" className="text-gray-600 hover:text-[#2B7A3D] transition-colors font-medium">Contato</a>
              <a href="#para-empresas" className="text-gray-600 hover:text-[#2B7A3D] transition-colors font-medium" data-testid="header-para-empresas-link">
                Seja Parceiro
              </a>
              <Link to="/orcamento" className="text-[#2B7A3D] hover:text-[#1f5a2c] transition-colors font-semibold" data-testid="header-orcamento-link">
                Orçamento online
              </Link>
            </nav>
            <div className="flex items-center gap-2 md:hidden">
              <Link to="/orcamento">
                <Button size="sm" className="bg-[#2B7A3D] hover:bg-[#1f5a2c] text-white" data-testid="header-orcamento-mobile">
                  <FileText size={18} />
                </Button>
              </Link>
              <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
                <Button size="sm" className="bg-[#25D366] hover:bg-[#1da851] text-white">
                  <MessageCircle size={18} />
                </Button>
              </a>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section - Side by Side */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-white min-h-screen flex items-center">
        <div className="max-w-7xl mx-auto w-full">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex flex-col items-start gap-2 mb-6">
                {/* TODO (pendente): reexibir badge "São Paulo Capital e Regiões Metropolitanas" quando a operação SP voltar */}
                <div className="inline-flex items-center gap-2 bg-[#2B7A3D]/10 text-[#2B7A3D] px-4 py-2 rounded-full text-sm font-medium" data-testid="badge-regiao-bh">
                  <CheckCircle size={16} />
                  Belo Horizonte e Regiões Metropolitanas
                </div>
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight" style={{ fontFamily: 'Work Sans' }}>
                Sua casa em{' '}
                <span className="text-[#2B7A3D]">boas mãos</span>
              </h1>
              
              <p className="mt-6 text-xl text-gray-600 leading-relaxed">
                Assistência residencial completa para pequenos reparos, instalações e manutenções. 
                Profissionais qualificados, atendimento rápido e preço justo.
              </p>

              {/* Stats */}
              <div className="flex gap-8 mt-8 mb-10">
                <div>
                  <p className="text-3xl font-bold text-[#2B7A3D]">500+</p>
                  <p className="text-gray-500 text-sm">Clientes atendidos</p>
                </div>
                <div>
                  <p className="text-3xl font-bold text-[#2B7A3D]">100%</p>
                  <p className="text-gray-500 text-sm">Satisfação</p>
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/orcamento" className="w-full sm:w-auto">
                  <Button size="lg" className="w-full sm:w-auto bg-[#2B7A3D] hover:bg-[#1f5a2c] text-white text-lg px-8 py-6 rounded-xl shadow-lg shadow-emerald-700/30" data-testid="orcamento-hero-btn">
                    <FileText className="mr-2" size={22} />
                    Solicitar Orçamento
                    <ArrowRight className="ml-2" size={20} />
                  </Button>
                </Link>
                <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto">
                  <Button size="lg" variant="outline" className="w-full sm:w-auto border-2 border-[#25D366] text-[#25D366] hover:bg-[#25D366] hover:text-white text-lg px-8 py-6 rounded-xl" data-testid="whatsapp-hero-btn">
                    <MessageCircle className="mr-2" size={22} />
                    Fale conosco
                  </Button>
                </a>
              </div>
            </div>
            
            {/* Image Carousel */}
            <div className="hidden lg:block relative">
              <div className="relative">
                {/* Images */}
                {heroImages.map((image, index) => (
                  <img 
                    key={index}
                    src={image.url} 
                    alt={image.alt}
                    className={`w-full h-[550px] object-cover rounded-3xl shadow-2xl absolute top-0 left-0 transition-opacity duration-700 ${
                      index === currentImage ? 'opacity-100' : 'opacity-0'
                    }`}
                    style={{ position: index === 0 ? 'relative' : 'absolute' }}
                  />
                ))}
                
                {/* Service badge */}
                <div className="absolute top-4 right-4 bg-[#2B7A3D] text-white px-4 py-2 rounded-full text-sm font-medium shadow-lg">
                  {heroImages[currentImage].service}
                </div>
                
                {/* Overlay card */}
                <div className="absolute -bottom-6 -left-6 bg-white p-5 rounded-2xl shadow-xl z-10">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-[#25D366] rounded-xl flex items-center justify-center">
                      <CheckCircle className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="font-bold text-gray-900">Garantia</p>
                      <p className="text-gray-500 text-sm">Em todos os serviços</p>
                    </div>
                  </div>
                </div>
                
                {/* Image indicators */}
                <div className="absolute -bottom-12 left-1/2 -translate-x-1/2 flex gap-2">
                  {heroImages.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentImage(index)}
                      className={`w-2 h-2 rounded-full transition-all ${
                        index === currentImage 
                          ? 'w-8 bg-[#2B7A3D]' 
                          : 'bg-gray-300 hover:bg-gray-400'
                      }`}
                    />
                  ))}
                </div>
                
                {/* Decorative element */}
                <div className="absolute -top-4 -right-4 w-24 h-24 bg-[#2B7A3D]/10 rounded-full -z-10"></div>
                <div className="absolute -bottom-8 -right-8 w-32 h-32 bg-[#25D366]/10 rounded-full -z-10"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="servicos" className="py-24 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-[#2B7A3D] font-semibold text-sm uppercase tracking-wider">O que fazemos</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mt-2" style={{ fontFamily: 'Work Sans' }}>
              Nossos Serviços
            </h2>
            <p className="mt-4 text-xl text-gray-600 max-w-2xl mx-auto">
              Soluções completas para manter sua casa sempre em ordem
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer group bg-white overflow-hidden">
                <CardContent className="p-8">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#2B7A3D] to-[#1E5F2D] flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                    <service.icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{service.title}</h3>
                  <p className="text-gray-600">{service.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section id="sobre" className="py-24 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-[#2B7A3D] font-semibold text-sm uppercase tracking-wider">Por que nos escolher</span>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mt-2 mb-6" style={{ fontFamily: 'Work Sans' }}>
                Qualidade e confiança em cada serviço
              </h2>
              <p className="text-gray-600 text-lg mb-8">
                Na Tenance, entendemos que sua casa é seu patrimônio mais valioso. Por isso, trabalhamos com dedicação e profissionalismo para entregar sempre o melhor resultado.
              </p>
              <div className="space-y-6">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-[#2B7A3D]/10 flex items-center justify-center flex-shrink-0">
                      <benefit.icon className="w-6 h-6 text-[#2B7A3D]" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">{benefit.title}</h3>
                      <p className="text-gray-600">{benefit.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1621905251189-08b45d6a269e?ixlib=rb-4.0.3&auto=format&fit=crop&w=1469&q=80" 
                alt="Profissional trabalhando"
                className="rounded-2xl shadow-2xl w-full"
              />
              <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-xl shadow-lg">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-[#25D366] rounded-full flex items-center justify-center">
                    <CheckCircle className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="font-bold text-gray-900">Garantia</p>
                    <p className="text-gray-600 text-sm">Em todos os serviços</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How it Works */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-[#2B7A3D] font-semibold text-sm uppercase tracking-wider">Processo simples</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mt-2" style={{ fontFamily: 'Work Sans' }}>
              Como funciona?
            </h2>
          </div>
          <div className="grid sm:grid-cols-3 gap-8 relative">
            {/* Connection line */}
            <div className="hidden sm:block absolute top-16 left-1/4 right-1/4 h-0.5 bg-gradient-to-r from-[#1E88C9] via-[#2B7A3D] to-[#3D4F5F]"></div>
            
            <div className="text-center relative">
              <div className="w-20 h-20 rounded-full bg-[#1E88C9] text-white text-2xl font-bold flex items-center justify-center mx-auto mb-6 relative z-10 shadow-lg shadow-[#1E88C9]/30">
                1
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Entre em contato</h3>
              <p className="text-gray-600">Envie uma mensagem pelo WhatsApp ou ligue para nós</p>
            </div>
            <div className="text-center relative">
              <div className="w-20 h-20 rounded-full bg-[#2B7A3D] text-white text-2xl font-bold flex items-center justify-center mx-auto mb-6 relative z-10 shadow-lg shadow-[#2B7A3D]/30">
                2
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Receba o orçamento</h3>
              <p className="text-gray-600">Avaliamos sua necessidade e enviamos um orçamento sem compromisso</p>
            </div>
            <div className="text-center relative">
              <div className="w-20 h-20 rounded-full bg-[#3D4F5F] text-white text-2xl font-bold flex items-center justify-center mx-auto mb-6 relative z-10 shadow-lg shadow-[#3D4F5F]/30">
                3
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Agendamos a visita</h3>
              <p className="text-gray-600">Escolha o melhor horário e nosso profissional vai até você</p>
            </div>
          </div>
        </div>
      </section>

      {/* Depoimentos */}
      <section id="depoimentos" className="py-24 px-4 sm:px-6 lg:px-8 bg-gray-50" data-testid="secao-depoimentos">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-[#2B7A3D] font-semibold text-sm uppercase tracking-wider">Confiança comprovada</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mt-2" style={{ fontFamily: 'Work Sans' }}>
              O que dizem clientes e parceiros
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { texto: 'Chamei para trocar o chuveiro e aproveitei para resolver duas tomadas. O profissional chegou no horário e deixou tudo limpo. Recomendo demais!', nome: 'Mariana S.', papel: 'Cliente • Santa Branca, BH', tipo: 'cliente' },
              { texto: 'Como síndico, era um sufoco achar prestador de confiança. Com a Tenance abro a solicitação no portal e só acompanho. Mudou minha rotina.', nome: 'Carlos H.', papel: 'Síndico parceiro', tipo: 'parceiro' },
              { texto: 'Orçamento rápido pelo WhatsApp, preço justo e garantia de 30 dias. A fechadura eletrônica ficou perfeita.', nome: 'Renato A.', papel: 'Cliente • Buritis, BH', tipo: 'cliente' },
              { texto: 'Nossa imobiliária usa a Tenance nas manutenções de entrega de chaves. O preço fechado antes da execução evita qualquer surpresa com proprietário.', nome: 'Juliana M.', papel: 'Imobiliária parceira', tipo: 'parceiro' },
              { texto: 'Atendimento educado do início ao fim. Avisaram quando o profissional estava a caminho e o serviço foi impecável.', nome: 'Fernanda L.', papel: 'Cliente • Castelo, BH', tipo: 'cliente' },
              { texto: 'A fatura mensal com todas as OS detalhadas facilitou muito nossa prestação de contas no condomínio.', nome: 'Roberto P.', papel: 'Administradora parceira', tipo: 'parceiro' },
            ].map((d) => (
              <div key={d.nome} className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm hover:shadow-md transition-shadow flex flex-col">
                <div className="flex items-center gap-0.5 mb-3">
                  {[...Array(5)].map((_, i) => (<Star key={i} size={15} className="text-amber-400 fill-amber-400" />))}
                </div>
                <p className="text-gray-600 text-sm leading-relaxed flex-1">“{d.texto}”</p>
                <div className="flex items-center gap-3 mt-5 pt-4 border-t border-gray-50">
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold ${d.tipo === 'parceiro' ? 'bg-[#2B7A3D]/10 text-[#2B7A3D]' : 'bg-[#1E88C9]/10 text-[#1E88C9]'}`}>
                    {d.nome.charAt(0)}
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-gray-900">{d.nome}</div>
                    <div className={`text-xs ${d.tipo === 'parceiro' ? 'text-[#2B7A3D] font-medium' : 'text-gray-400'}`}>{d.papel}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Para Empresas / Programa de Parceria */}
      <section id="para-empresas" className="py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#1f4a2c] to-[#2B7A3D] relative overflow-hidden" data-testid="secao-para-empresas">
        <div className="absolute -top-24 -right-24 w-96 h-96 bg-white/5 rounded-full"></div>
        <div className="absolute -bottom-32 -left-16 w-80 h-80 bg-white/5 rounded-full"></div>
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-flex items-center gap-2 bg-white/10 text-emerald-100 px-4 py-2 rounded-full text-xs font-semibold uppercase tracking-wider mb-5">
                <Building2 size={14} /> Programa de Parceria
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Work Sans' }}>
                Sua imobiliária ou condomínio sem dor de cabeça com manutenção
              </h2>
              <p className="text-emerald-50/90 mb-8">
                Imobiliárias, administradoras, condomínios, síndicos e corretores contam com a Tenance
                para cuidar dos seus imóveis — Com portal próprio e preço fechado.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link to="/sejaparceiro">
                  <Button size="lg" className="w-full sm:w-auto bg-white text-[#2B7A3D] hover:bg-emerald-50 font-bold text-base px-8 py-6 rounded-xl shadow-lg" data-testid="cta-quero-ser-parceiro">
                    Quero ser parceiro
                    <ArrowRight size={18} className="ml-2" />
                  </Button>
                </Link>
                <Link to="/parceiro">
                  <Button size="lg" variant="outline" className="w-full sm:w-auto border-2 border-white/40 bg-transparent text-white hover:bg-white/10 hover:text-white text-base px-8 py-6 rounded-xl" data-testid="cta-acessar-portal">
                    Já sou parceiro — Acessar portal
                  </Button>
                </Link>
              </div>
              <a href="/sejaparceiro" className="inline-flex items-center gap-1.5 text-emerald-100/80 hover:text-white text-sm mt-6 transition-colors" data-testid="link-prestador">
                <WrenchIcon size={14} /> É prestador de serviço? Venha trabalhar com a gente <ArrowRight size={14} />
              </a>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              {[
                { icon: LayoutDashboard, titulo: 'Portal exclusivo', texto: 'Abra solicitações e acompanhe cada serviço online, em tempo real.' },
                { icon: CreditCard, titulo: 'Limite de crédito', texto: 'Serviços executados primeiro, pagamento na fatura semanal ou mensal.' },
                { icon: Receipt, titulo: 'Preço fechado', texto: 'Valor aprovado por você antes da execução. Sem surpresa.' },
                { icon: BadgeCheck, titulo: 'Equipe verificada', texto: 'Profissionais qualificados com garantia em todos os serviços.' },
              ].map((b) => (
                <div key={b.titulo} className="bg-white/10 backdrop-blur-sm border border-white/15 rounded-2xl p-5 hover:bg-white/15 transition-colors">
                  <div className="w-11 h-11 rounded-xl bg-white/15 flex items-center justify-center mb-3">
                    <b.icon size={21} className="text-white" />
                  </div>
                  <div className="font-bold text-white text-sm mb-1">{b.titulo}</div>
                  <div className="text-emerald-50/80 text-xs leading-relaxed">{b.texto}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="contato" className="py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#3D4F5F] to-[#2B3A47] relative overflow-hidden">
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#25D366] rounded-full blur-3xl translate-x-1/2 translate-y-1/2"></div>
        </div>
        
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <h2 className="text-3xl sm:text-5xl font-bold text-white mb-6" style={{ fontFamily: 'Work Sans' }}>
            Precisa de ajuda em casa?
          </h2>
          <p className="text-xl text-gray-300 mb-10 max-w-2xl mx-auto">
            Entre em contato agora mesmo e solicite seu orçamento gratuito. Atendemos com rapidez e qualidade!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
              <Button size="lg" className="w-full sm:w-auto bg-[#25D366] hover:bg-[#1da851] text-white text-lg px-10 py-6 rounded-full shadow-lg shadow-[#25D366]/30" data-testid="whatsapp-cta-btn">
                <MessageCircle className="mr-2" size={24} />
                Chamar no WhatsApp
              </Button>
            </a>
          </div>
          <p className="mt-8 text-gray-400 flex items-center justify-center gap-2">
            <Phone size={18} />
            (31) 97338-1052
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-900">
        <div className="max-w-7xl mx-auto">
          <div className="grid sm:grid-cols-3 gap-12">
            <div>
              <img src="/logo-footer.png" alt="Tenance" className="h-28 mb-4" />
              <p className="text-gray-400">
                Assistência residencial completa para sua casa. Qualidade e confiança em cada serviço.
              </p>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Serviços</h4>
              <ul className="space-y-3 text-gray-400">
                <li className="hover:text-white transition-colors cursor-pointer">Elétrica</li>
                <li className="hover:text-white transition-colors cursor-pointer">Hidráulica</li>
                <li className="hover:text-white transition-colors cursor-pointer">Pintura</li>
                <li className="hover:text-white transition-colors cursor-pointer">Desmontagem e Montagem de Móveis</li>
                <li className="hover:text-white transition-colors cursor-pointer">Reparos Gerais</li>
                <li>
                  <Link to="/sejaparceiro" className="text-[#6fcf8a] hover:text-white transition-colors font-medium" data-testid="footer-seja-parceiro-link">
                    Seja parceiro Tenance →
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Contato</h4>
              <ul className="space-y-3 text-gray-400">
                <li className="flex items-center gap-2">
                  <Phone size={16} className="text-[#25D366]" />
                  (31) 97338-1052
                </li>
                <li className="flex items-center gap-2">
                  <MessageCircle size={16} className="text-[#25D366]" />
                  WhatsApp disponível
                </li>
                <li>
                  <a href="https://instagram.com/tenance.br" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:text-white transition-colors">
                    <Instagram size={16} className="text-[#E4405F]" />
                    @tenance.br
                  </a>
                </li>
              </ul>
              <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="inline-block mt-4">
                <Button className="bg-[#25D366] hover:bg-[#1da851] text-white rounded-full">
                  <MessageCircle size={18} className="mr-2" />
                  Fale Conosco
                </Button>
              </a>
            </div>
          </div>
          <div className="mt-12 pt-8 border-t border-gray-800 text-center text-gray-500">
            <p>&copy; {new Date().getFullYear()} Tenance - Assistência Residencial. Todos os direitos reservados. - Desenvolvido por <a href="https://saggar.com.br" target="_blank" rel="noopener noreferrer" className="text-[#25D366] hover:underline">Saggar Marketing</a></p>
          </div>
        </div>
      </footer>

      {/* Floating WhatsApp Button */}
      <a
        href={WHATSAPP_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 w-16 h-16 bg-[#25D366] rounded-full flex items-center justify-center shadow-lg shadow-[#25D366]/40 hover:scale-110 transition-transform z-50 animate-pulse"
        data-testid="whatsapp-float-btn"
      >
        <MessageCircle className="w-8 h-8 text-white" />
      </a>
    </div>
  );
};

export default LandingPage;
